import Section from '@components/atoms/Section';
import BreadCrumbs from '@components/molecules/BreadCrumbs';
import PageContainer from '@components/molecules/PageContainer';
import RichTextSection from '@components/molecules/RichTextSection';
import DefaultTable from '@components/molecules/table/DefaultTable';
import DefaultHeader from '@components/organisms/headers/DefaultHeader';
import JoinSection from '@components/organisms/sections/JoinSection';
import COLORS from '@lib/constants/colors';
import ROUTES from '@lib/constants/routes';
import { aggregateRating } from '@lib/constants/seo';
import carriersContent from '@lib/data/contents/carriersContent';
import { getInsurancePageInfo, InsurancePageId } from '@lib/helpers/texts';
import { GetStaticPaths, GetStaticProps } from 'next';
import { ProductJsonLd } from 'next-seo';
import Link from 'next/link';
import React from 'react';
import styled from 'styled-components';

interface Props {
  insurancePage: InsurancePageId;
}

const List = styled.ol`
  &&& {
    margin: 0;
    list-style: none;
    position: relative;
    padding: 0;

    li {
      margin-bottom: 32px;
      h3 {
        margin-bottom: 0px;
      }
    }
  }
`;

const Page = ({ insurancePage }: Props) => {
  const info = getInsurancePageInfo(insurancePage);

  if (!info) return null;

  const carriers = Object.values(carriersContent)
    .filter((c) => c.products.some((p) => p.pageId === insurancePage))
    .sort(
      (a, b) =>
        Number(b.reclameAqui.performanceData.consumerScore) -
        Number(a.reclameAqui.performanceData.consumerScore),
    );

  return (
    <PageContainer
      title={`Melhores seguradoras para ${info.name}`}
      metaDescription={`Confira quais as melhores seguradoras para ${info.name} que oferecem ${info.productName}, veja os preços, avaliações e muito mais`}
      transparentNavbar
      transparentNavbarFontColor={COLORS.black}
      customAmpPage
    >
      <ProductJsonLd
        productName={`Seguradoras de ${info.name}`}
        description={`Melhores seguradoras de ${info.name}. Cote, contrate ou renove o seguro em menos de 3 minutos!`}
        aggregateRating={aggregateRating}
      />
      <DefaultHeader
        pastel
        title={`Quais as melhores seguradoras de ${info.name}?`}
        subtitle={`Encontre as melhores ofertas de seguro de ${info.name} por um preço justo. Faça uma cotação de ${info.productName} gratuita e 100% online.
`}
        buttonText="Cotar Seguro Online"
      />
      <RichTextSection>
        <p>
          Procurando por uma seguradora de {info.name} confiável? Reunimos as
          melhores seguradoras que operam no Brasil. Confira!
        </p>
        <h2>Lista das seguradoras de {info.name} no Brasil</h2>
        <List>
          {carriers.map((c) => (
            <li key={c.id}>
              <h3>
                <Link href={ROUTES.insurancePage.carrier(insurancePage, c.id)}>
                  <a>{c.name}</a>
                </Link>
              </h3>
              <p>{c.about}</p>
            </li>
          ))}
        </List>
        <h2>
          Qual seguradora é a melhor? Todas as principais seguradoras, revisadas
          em profundidade:
        </h2>
        <p>
          Quer saber qual é a seguradora de {info.name} mais confiável? Os
          especialistas em seguros da Luna estão aqui para ajudar. Reunimos
          dados confiáveis de terceiros sobre as seguradoras mais populares do
          Brasil e elaboramos avaliações imparciais de cada seguradora.
        </p>
        <p>
          Cada análise da seguradora inclui a pontuação geral do Reclame Aqui,
          bem como a nota que seus clientes dão para os serviços prestados.
        </p>
        <h3>
          Avaliações de seguradoras de {info.name} para{' '}
          {new Date().getFullYear()}
        </h3>
        <DefaultTable
          heads={['Seguradora', 'Nota dos Clientes', 'Nota Reclame Aqui']}
          rows={carriers.map((c) => [
            c.name,
            c.reclameAqui.performanceData.consumerScore,
            c.reclameAqui.panel.avg,
          ])}
        />
      </RichTextSection>
      <Section style={{ paddingTop: 0 }}>
        <BreadCrumbs
          links={[
            {
              label: 'Luna',
              href: ROUTES.root,
            },
            {
              label: info?.name || '',
              href: ROUTES.insurancePage.home(insurancePage),
            },
            {
              label: 'Seguradoras',
              href: ROUTES.insurancePage.carriers(insurancePage),
            },
          ]}
        />
      </Section>
      <JoinSection
        buttonText="Cotar meu seguro"
        title="Contratação de seguro online"
        description="Cote o seguro de moto e carro nas principais seguradoras de forma totalmente digital"
      />
    </PageContainer>
  );
};

export const getStaticPaths: GetStaticPaths = async () => ({
  paths: ['/seguro-carro/seguradoras', '/seguro-moto/seguradoras'],
  fallback: false,
});

export const getStaticProps: GetStaticProps<Props> = async ({ params }) => {
  const { insurancePage } = params as {
    insurancePage: InsurancePageId;
  };

  return {
    props: { insurancePage },
    revalidate: 3600,
  };
};
export default Page;
